import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { emailWithTldValidator } from 'src/app/validators/email-tld.validator';
import { cpfValidator, cpfCnpjValidator } from 'src/app/validators/cpf-cnpj.validator';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  faChevronLeft, 
  faFileAlt, 
  faUser,
  faCheck,
  faDownload,
  faCopy,
  faSpinner,
  faTimes,
  faPaperPlane,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { IFormFieldsRequest, IFormFieldsAnswersRequest, IFormStructure } from 'src/app/models/form.interface';
import { IDocumentFilesResponse, IDocumentFileResponse } from 'src/app/models/document.model';
import { DocumentsService } from 'src/app/services/documents.service';
import { DownloadService } from 'src/app/services/download.service';
import { AuthService } from 'src/app/services/auth.service';
import { Store } from '@ngrx/store';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { TemplatesService } from 'src/app/services/templates.service';
import { ModulesService } from 'src/app/services/modules.service';

@Component({
  selector: 'app-document-form-v2',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.sass']
})
export class DocumentFormV2Component implements OnInit {
  // Icons
  faChevronLeft = faChevronLeft;
  faFileAlt = faFileAlt;
  faUser = faUser;
  faCheck = faCheck;
  faDownload = faDownload;
  faCopy = faCopy;
  faSpinner = faSpinner;
  faTimes = faTimes;
  faPaperPlane = faPaperPlane;
  faSearch = faSearch;

  // State
  loading = false;
  submitting = false;
  currentStep: 'form' | 'download' = 'form';
  activeSection = 'informacoes';
  showConfirmSendModal = false;

  // Multiple select filter state
  multiSelectFilters: { [fieldName: string]: string } = {};

  // Procedure selection modal state
  procedureModalOpen = false;
  procedureModalFieldName = '';

  // Calculator v3 state
  hasCalculatorPermission = false;
  divergenceChecked = false;
  recommendedProcedures: string[] = [];
  recommendedModalOpen = false;

  // Form data - usando FormGroup dinâmico como no form antigo
  dynamicForm: FormGroup = this.fb.group({});
  formStructure: IFormStructure[] = [];
  documents: IFormFieldsRequest = { document_ids: [] };
  anamneseId = '';
  patientId = '';

  // Download data
  generatedDocsResponse: IDocumentFilesResponse | null = null;
  filesToSign: IDocumentFileResponse[] = [];
  sentStatus = 'NOT_SENT';
  btnLoadings: string[] = [];
  disableAllButtons = false;
  sendDocType = 'DELIVERY_METHOD_SMS';
  treatmentPlanId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private documentsService: DocumentsService,
    private templatesService: TemplatesService,
    private downloadService: DownloadService,
    private authService: AuthService,
    private store: Store,
    public modulesService: ModulesService
  ) {
    this.sendDocType = this.authService.getSendDocsType();
    this.hasCalculatorPermission = this.authService.getCalculatorPermission() || false;
  }

  ngOnInit(): void {
    this.loadFormFields();
  }

  loadFormFields(): void {
    this.loading = true;

    // Get params from query
    const requestParam = this.route.snapshot.queryParams['request'];
    this.anamneseId = this.route.snapshot.queryParams['anamneseId'] || '';
    this.patientId = this.route.snapshot.queryParams['patientId'] || '';

    if (requestParam) {
      try {
        this.documents = JSON.parse(atob(requestParam)) as IFormFieldsRequest;
      } catch (e) {
        console.error('Error parsing request:', e);
      }
    }

    // Load fields from API
    this.templatesService.fields(this.documents, this.anamneseId, this.patientId)
      .then((response) => {
        this.formStructure = response;
        this.buildDynamicForm();
        // If navigation provided prefilled fields (e.g. nomeprocedimento), apply them to the dynamic form
        const prefilled = (this.documents as any)?.fields as Array<{ name: string; value: any }> | undefined;
        if (prefilled && Array.isArray(prefilled)) {
          prefilled.forEach(f => {
            if (!f || !f.name) return;
            const name = String(f.name);
            // Try exact match first
            if (this.dynamicForm.contains(name)) {
              this.dynamicForm.get(name)?.setValue(f.value);
            } else {
              // Fallback to case-insensitive match
              const match = Object.keys(this.dynamicForm.controls).find(k => k.toLowerCase() === name.toLowerCase());
              if (match) this.dynamicForm.get(match)?.setValue(f.value);
            }
          });
        }
      })
      .catch((err: unknown) => console.error('Error loading form fields:', err))
      .finally(() => {
        this.loading = false;
      });
  }

  buildDynamicForm(): void {
    // Criar controles dinâmicos para cada campo - igual ao form antigo
    this.formStructure.forEach((control) => {
      // nomeProcedimentosRecomendados is programmatically filled before submission, not user-editable
      if (control.name === 'nomeProcedimentosRecomendados') return;
      const validators = [];
      
      if (control.required_error_message) {
        validators.push(Validators.required);
      }
      if (control.label?.toLowerCase().includes('email')) {
        // Use stricter validator that requires a valid TLD (eg .com, .com.br)
        validators.push(emailWithTldValidator());
      }
      if (control.name.includes('telefonepaciente')) {
        validators.push(Validators.minLength(8));
      }
      if (control.name.toLowerCase().includes('cpf')) {
        validators.push(this.modulesService.moduleName === 'ALUNOS' ? cpfCnpjValidator() : cpfValidator());
      }

      let formControl: FormControl;

      if (control.type === 'multiple-select') {
        // Para multiple-select, usar array de valores
        const initialValues = Array.isArray(control.value)
          ? control.value.map((v: unknown) => String(v)).filter((v: string) => v !== '')
          : [];
        formControl = new FormControl(initialValues, validators);
      } else {
        // Para outros tipos, usar string
        let value = control.value?.toString() || '';
        if (control.name.includes('cpf')) {
          value = this.setCpfCnpjMask(value);
        }
        if (control.name.includes('rg')) {
          value = this.setRgMask(value);
        }
        if (control.label?.toLowerCase().includes('valor')) {
          value = this.formatCurrency(value);
        }
        formControl = new FormControl(value, validators);
      }

      this.dynamicForm.addControl(control.name, formControl);

      // Se for um select de profissional com apenas uma opção, preencher por padrão
      try {
        const label = (control.label || '').toString().toLowerCase();
        const name = (control.name || '').toString().toLowerCase();
        const isProfessionalField = label.includes('profissional') || label.includes('nome completo') || name.includes('profissional') || name.includes('professional');
        if (control.type === 'select' && (control.options || []).length === 1 && isProfessionalField) {
          const singleOption = control.options![0];
          // Usa value da opção (string) como valor do form control
          const valueToSet = singleOption && singleOption.value !== undefined ? String(singleOption.value) : (singleOption && singleOption.label ? String(singleOption.label) : '');
          this.dynamicForm.get(control.name)?.setValue(valueToSet);
        }
      } catch (e) {
        // Não bloquear o fluxo se algo falhar
        console.warn('Erro ao aplicar seleção padrão para campo select:', e);
      }
    });

    this.dynamicForm.markAllAsTouched();
  }

  // ========== MULTIPLE SELECT METHODS ==========

  isOptionSelected(fieldName: string, optionValue: unknown): boolean {
    const value = this.dynamicForm.get(fieldName)?.value;
    if (Array.isArray(value)) {
      return value.includes(String(optionValue));
    }
    return false;
  }

  getFilteredOptions(field: IFormStructure): { value: unknown; label: string }[] {
    const filterTerm = this.multiSelectFilters[field.name] || '';
    if (!filterTerm) {
      return field.options || [];
    }
    const term = filterTerm.toLowerCase();
    return (field.options || []).filter(option => 
      option.label.toLowerCase().includes(term)
    );
  }

  onMultiSelectFilterChange(fieldName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.multiSelectFilters[fieldName] = input.value;
  }

  onMultiSelectChange(fieldName: string, optionValue: unknown, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const currentValue: string[] = this.dynamicForm.get(fieldName)?.value || [];
    const valueStr = String(optionValue);

    if (checkbox.checked) {
      if (!currentValue.includes(valueStr)) {
        this.dynamicForm.get(fieldName)?.setValue([...currentValue, valueStr]);
      }
    } else {
      this.dynamicForm.get(fieldName)?.setValue(currentValue.filter(v => v !== valueStr));
    }

    // Se o campo é is_info_base, buscar valores automáticos
    const field = this.formStructure.find(f => f.name === fieldName);
    if (field?.is_info_base) {
      this.fetchFilledControls(fieldName, field);
    }
  }

  // Retorna os rótulos e valores das opções selecionadas para exibir como 'chips' acima do input de busca
  getSelectedOptionsLabels(field: IFormStructure): { value: string; label: string }[] {
    const selected: string[] = this.dynamicForm.get(field.name)?.value || [];
    if (!Array.isArray(selected) || selected.length === 0) return [];
    return (field.options || [])
      .filter(opt => selected.includes(String(opt.value)))
      .map(opt => ({ value: String(opt.value), label: opt.label }));
  }

  // Retorna os rótulos das opções selecionadas concatenados em uma string (ex: "Proc A, Proc B e Proc C")
  getSelectedOptionsText(field: IFormStructure): string {
    const labels = this.getSelectedOptionsLabels(field).map(l => l.label || '').filter(l => l.length > 0);
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return labels.join(' e ');
    return labels.slice(0, -1).join(', ') + ' e ' + labels[labels.length - 1];
  }

  // Remove um valor selecionado (chamada ao clicar no chip)
  removeSelectedOption(fieldName: string, value: string): void {
    const currentValue: string[] = this.dynamicForm.get(fieldName)?.value || [];
    const newValue = currentValue.filter(v => v !== String(value));
    this.dynamicForm.get(fieldName)?.setValue(newValue);
    const field = this.formStructure.find(f => f.name === fieldName);
    if (field?.is_info_base) {
      this.fetchFilledControls(fieldName, field);
    }
  }

  fetchFilledControls(fieldName: string, control: IFormStructure): void {
    const selectedValues = this.dynamicForm.get(fieldName)?.value as string[] || [];
    
    if (selectedValues.length === 0) return;

    this.templatesService.getFilledControls(selectedValues, control)
      .then((controls) => {
        controls.forEach((newControl) => {
          if (this.dynamicForm.controls[newControl.name] && newControl.name !== control.name) {
            let value = newControl.value?.toString() || '';

            // Formatar valores monetários
            if (newControl.label?.toLowerCase().includes('valor')) {
              value = this.formatCurrency(value);
            }

            this.dynamicForm.controls[newControl.name].setValue(value);
            this.dynamicForm.controls[newControl.name].markAsDirty();
          }
        });
      })
      .catch((err: unknown) => console.error('Error fetching filled controls:', err));
  }

  // ========== PROCEDURE MODAL METHODS ==========

  isProcedureField(field: IFormStructure): boolean {
    return field.type === 'multiple-select' && (field.name || '').toLowerCase().includes('nomeprocedimento');
  }

  openProcedureModal(fieldName: string): void {
    this.procedureModalFieldName = fieldName;
    this.procedureModalOpen = true;
  }

  onProcedureModalConfirm(selected: string[]): void {
    this.dynamicForm.get(this.procedureModalFieldName)?.setValue(selected);
    // Remove declined items that are now contracted
    this.recommendedProcedures = this.recommendedProcedures.filter(v => !selected.includes(v));
    const field = this.formStructure.find(f => f.name === this.procedureModalFieldName);
    if (field?.is_info_base) {
      this.fetchFilledControls(this.procedureModalFieldName, field);
    }
    this.procedureModalOpen = false;
  }

  onProcedureModalCancel(): void {
    this.procedureModalOpen = false;
    this.recommendedModalOpen = false;
  }

  removeProcedure(fieldName: string, value: string): void {
    this.removeSelectedOption(fieldName, value);
  }

  // ========== RECOMMENDED PROCEDURES METHODS ==========

  openRecommendedModal(field: IFormStructure): void {
    this.procedureModalFieldName = field.name;
    this.recommendedModalOpen = true;
  }

  onRecommendedModalConfirm(selected: string[]): void {
    this.recommendedProcedures = selected;
    this.recommendedModalOpen = false;
  }

  onRecommendedModalCancel(): void {
    this.recommendedModalOpen = false;
  }

  removeRecommendedProcedure(value: string): void {
    this.recommendedProcedures = this.recommendedProcedures.filter(v => v !== value);
  }

  getRecommendedLabels(field: IFormStructure): { value: string; label: string }[] {
    if (!this.recommendedProcedures.length) return [];
    return (field.options || [])
      .filter(opt => this.recommendedProcedures.includes(String(opt.value)))
      .map(opt => ({ value: String(opt.value), label: opt.label }));
  }

  getDeclinableOptions(field: IFormStructure): { label: string; value: string | number | boolean }[] {
    const contracted: string[] = this.dynamicForm.get(field.name)?.value || [];
    return (field.options || []).filter(opt => !contracted.includes(String(opt.value)));
  }

  private formatProcedureList(labels: string[]): string {
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return labels.slice(0, -1).join(', ') + ' e ' + labels[labels.length - 1];
  }

  // ========== MASKS ==========

  getMask(field: IFormStructure): string {
    const label = field.label?.toLowerCase() || '';
    const name = field.name?.toLowerCase() || '';
    
    if (label.includes('cpf') || name.includes('cpf')) {
      if (this.modulesService.moduleName === 'ALUNOS') {
        return '';  // No static mask for ALUNOS — allows both CPF and CNPJ input freely
      }
      return '000.000.000-00';
    }
    if (label.includes('cep') || name.includes('cep')) {
      return '00000-000';
    }
    return '';
  }

  getInputMode(field: IFormStructure): string | null {
    const label = field.label?.toLowerCase() || '';
    const name = field.name?.toLowerCase() || '';
    
    if (name.includes('telefone') || label.includes('telefone') || name.includes('phone') || label.includes('phone')) {
      return 'tel';
    }
    if (label.includes('cpf') || name.includes('cpf') || label.includes('cnpj') || name.includes('cnpj')) {
      return 'numeric';
    }
    return null;
  }

  isCurrencyField(field: IFormStructure): boolean {
    const label = field.label?.toLowerCase() || '';
    const name = field.name?.toLowerCase() || '';
    return label.includes('valor') || name.includes('valor') || 
           label.includes('preço') || name.includes('preco') ||
           label.includes('total') || name.includes('total');
  }

  isPhoneField(field: IFormStructure): boolean {
    const name = (field.name || '').toLowerCase();
    const label = (field.label || '').toLowerCase();
    return name.includes('telefone') || name.includes('phone') ||
           label.includes('telefone') || label.includes('phone');
  }

  onPhoneInput(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    input.value = digits;
    this.dynamicForm.get(fieldName)?.setValue(digits, { emitEvent: false });
  }

  onCurrencyInput(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Remove tudo exceto números
    const numericValue = value.replace(/\D/g, '');
    
    if (!numericValue) {
      this.dynamicForm.get(fieldName)?.setValue('', { emitEvent: false });
      input.value = '';
      return;
    }
    
    // Converte para centavos e depois para reais
    const cents = parseInt(numericValue, 10);
    const reais = cents / 100;
    
    // Formata como moeda brasileira
    const formatted = reais.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
    
    this.dynamicForm.get(fieldName)?.setValue(formatted, { emitEvent: false });
    input.value = formatted;
  }

  isReadonlyField(field: IFormStructure): boolean {
    // Nenhum campo é readonly - todos podem ser editados pelo usuário
    return false;
  }

  formatCurrency(value: string): string {
    if (!value) return '';
    // Remove formatação existente
    const cleanValue = value.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.');
    const numericValue = parseFloat(cleanValue);
    if (isNaN(numericValue)) return value;
    
    // Formatar como moeda brasileira
    return numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  setCpfCnpjMask(value: string): string {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  setRgMask(value: string): string {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 9) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4');
    }
    return value;
  }

  goBack(): void {
    // Volta para a página anterior
    window.history.back();
  }

  onSubmit(): void {
    if (this.dynamicForm.invalid) {
      this.dynamicForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Preencha todos os campos obrigatórios.'));
      return;
    }

    // Validar recusados obrigatórios quando divergência marcada
    if (this.hasCalculatorPermission && this.divergenceChecked && this.recommendedProcedures.length === 0) {
      this.store.dispatch(setWarningText('Selecione pelo menos um procedimento recusado.'));
      return;
    }

    this.submitting = true;

    // Build request igual ao form antigo
    const formValue = this.dynamicForm.value;
    const fields: IFormStructure[] = Object.keys(formValue).map(key => 
      this.toFormStructure(key, this.trimResponse(formValue[key]))
    );

    // Adicionar nomeProcedimentosRecomendados se calculadora habilitada
    if (this.hasCalculatorPermission) {
      let recommendedValue: string;
      if (!this.divergenceChecked) {
        recommendedValue = 'declara que contratou todos os procedimentos recomendados, optando pela realização integral do plano de tratamento indicado pelos profissionais da CONTRATADA';
      } else {
        // Plano recomendado = contratados + recusados (plano completo original)
        const procedureField = this.formStructure.find(f => this.isProcedureField(f));
        const contracted: string[] = this.dynamicForm.get(procedureField?.name || '')?.value || [];
        const allRecommended = [...contracted, ...this.recommendedProcedures];
        const allLabels = (procedureField?.options || [])
          .filter(opt => allRecommended.includes(String(opt.value)))
          .map(opt => opt.label);
        const listText = this.formatProcedureList(allLabels);
        recommendedValue = `declara que não contratou todos os procedimentos recomendados, optando pela realização parcial do plano de tratamento.\nPlano de tratamento recomendado: ${listText}`;
      }
      const recommendedField = this.formStructure.find(
        f => f.name === 'nomeProcedimentosRecomendados'
      );
      if (recommendedField) {
        fields.push({ ...recommendedField, value: recommendedValue });
      }
    }

    const request: IFormFieldsAnswersRequest = {
      template_ids: this.documents.document_ids,
      fields: fields,
      anamnese_id: this.anamneseId,
      patient_id: this.patientId
    };

    

    this.documentsService.generate(request)
      .then((response: unknown) => {
       
        // Cast response to expected format
        const generatedResponse = response as IDocumentFilesResponse;
      
        
        // Gerar o state param igual ao layout antigo
        const stateParam = btoa(JSON.stringify(generatedResponse));
        
        // Navegar para a página de sucesso
        this.router.navigate(['main', 'v2', 'patients', 'document-form', 'success'], {
          queryParams: {
            state: stateParam,
            patient_id: this.patientId
          }
        });
      })
      .catch((error: unknown) => {
        console.error('Error generating documents:', error);
        this.store.dispatch(setWarningText('Erro ao gerar documentos. Tente novamente.'));
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  getFormValidationErrors(): { [key: string]: string[] } {
    const errors: { [key: string]: string[] } = {};
    Object.keys(this.dynamicForm.controls).forEach(key => {
      const control = this.dynamicForm.get(key);
      if (control?.errors) {
        errors[key] = Object.keys(control.errors);
      }
    });
    return errors;
  }

  toFormStructure(key: string, value: string | string[]): IFormStructure {
    // Normalize value based on field type (same as legacy form)
    let normalizedValue: string | string[] = value;

    if (key === 'paistelefonepaciente') {
      // Remove everything except numbers and +
      normalizedValue = String(value || '').replace(/[^0-9+]/g, '');
    }

    if (key.includes('valor')) {
      // Remove currency formatting: "R$ X.XXX,XX" -> "X.XXX,XX"
      if (typeof value === 'string' && value.includes('R$')) {
        normalizedValue = value.replace(/R\$\s?/g, '').replace(/\./g, '');
      }
    }

    if (key.includes('nomeProcedimento')) {
      // Convert array to comma-separated string with "e" for last item
      const normalizedProcedures = this.normalizeMultipleSelectValue(value);
      if (normalizedProcedures.length === 0) {
        normalizedValue = '';
      } else if (normalizedProcedures.length === 1) {
        normalizedValue = normalizedProcedures[0];
      } else {
        normalizedValue = normalizedProcedures.join(', ').replace(/,([^,]*)$/, ' e$1');
      }
    }

    return {
      ...this.formStructure.find((formStructure) => formStructure.name === key),
      value: normalizedValue
    } as IFormStructure;
  }

  private normalizeMultipleSelectValue(rawValue: unknown): string[] {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map(value => value !== undefined && value !== null ? String(value).trim() : '')
        .filter(value => value !== '');
    }

    if (typeof rawValue === 'string') {
      if (rawValue === '') {
        return [];
      }
      return rawValue
        .split(',')
        .map(value => value.trim())
        .filter(value => value !== '');
    }

    if (rawValue === undefined || rawValue === null) {
      return [];
    }

    const singleValue = String(rawValue).trim();
    return singleValue === '' ? [] : [singleValue];
  }

  trimResponse(response: unknown): string | string[] {
    if (typeof response === 'string') {
      return response.trim();
    }
    if (Array.isArray(response)) {
      return response;
    }
    return String(response || '');
  }

  // ========== DOWNLOAD METHODS ==========

  onClickDownloadFile(index: number, link: string, fileName: string, type: 'pdf' | 'docx'): void {
    if (!link) return;
    
    const loadingKey = `${index}-${type}`;
    this.btnLoadings.push(loadingKey);

    this.downloadService.downloadFile(link, `${fileName}`, type)
      .then(() => {
        this.btnLoadings = this.btnLoadings.filter(k => k !== loadingKey);
      })
      .catch((error: unknown) => {
        console.error('Error downloading file:', error);
        this.btnLoadings = this.btnLoadings.filter(k => k !== loadingKey);
      });
  }

  isDownloading(index: number, type: string): boolean {
    return this.btnLoadings.includes(`${index}-${type}`);
  }

  sendAllDocuments(): void {
    // Abre o modal de confirmação
    this.showConfirmSendModal = true;
  }

  closeConfirmSendModal(): void {
    this.showConfirmSendModal = false;
  }

  getDocumentsToSend(): IDocumentFileResponse[] {
    if (!this.generatedDocsResponse) return [];
    return this.generatedDocsResponse.documents_link_info
      .filter(doc => !this.filesToSign.some(f => f.document_id === doc.document_id));
  }

  confirmSendDocuments(): void {
    if (!this.generatedDocsResponse) return;
    
    this.showConfirmSendModal = false;
    this.sentStatus = 'SENDING';
    
    const documentsToSend = this.getDocumentsToSend();

    if (documentsToSend.length === 0) {
      this.sentStatus = 'SENT';
      return;
    }

    const documentIdList = documentsToSend.map(d => d.document_id);
    
    this.documentsService.sendDocumentToSign(documentIdList, this.generatedDocsResponse, this.patientId)
      .then((response) => {
        this.sentStatus = 'SENT';
        this.filesToSign = [...this.filesToSign, ...documentsToSend];
        
        // Captura o treatment_plan_id se retornado
        if (response?.treatment_plan_id) {
          this.treatmentPlanId = response.treatment_plan_id;
        }
      })
      .catch((error: any) => {
        console.error('Error sending documents:', error);
        this.sentStatus = 'NOT_SENT';
        const errorMessage = error?.message || 'Erro ao enviar documentos. Tente novamente.';
        this.store.dispatch(setWarningText(errorMessage));
      });
  }

  viewTreatmentPlan(): void {
    if (this.treatmentPlanId) {
      this.router.navigate(['main', 'v2', 'treatment-plan', this.treatmentPlanId]);
    }
  }

  getSendButtonText(): string {
    const texts: { [key: string]: string } = {
      'NOT_SENT': 'Enviar todos os documentos',
      'SENDING': 'Enviando...',
      'SENT': 'Documentos enviados!'
    };
    return texts[this.sentStatus] || 'Enviar todos os documentos';
  }

  canSendDocuments(): boolean {
    return this.sentStatus === 'NOT_SENT' && 
           this.generatedDocsResponse !== null && 
           this.generatedDocsResponse.documents_link_info.length > 0;
  }
}
